import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API, getAuthHeaders, handleApiError } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Card } from '../../components/ui/card';
import { Plus, CheckCircle2, MessageCircle, Trash2, Archive, RotateCcw, MapPin, Phone, Search, User, Edit2, Clock, Package, Pill, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { usePrintShortcut, generatePrintContent, openPrintWindow, formatPrintCurrency } from '../../utils/printUtils';

// Medicine Autocomplete Component with proper UX
const MedicineAutocomplete = ({ value, onChange, placeholder = "e.g., Paracetamol 500mg" }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && 
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search function
  const searchMedicines = useCallback(async (query) => {
    if (query.length < 1) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/medicines/search?q=${encodeURIComponent(query)}`, {
        headers: getAuthHeaders()
      });
      setSuggestions(response.data || []);
      setShowDropdown(response.data && response.data.length > 0);
    } catch (error) {
      console.error('Failed to search medicines:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue); // Update parent for form state
    setHighlightedIndex(-1);
    
    // Clear existing timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Debounce the search (300ms)
    debounceRef.current = setTimeout(() => {
      searchMedicines(newValue);
    }, 300);
  };

  // Handle selection from dropdown
  const handleSelect = (medicine) => {
    if (inputRef.current) {
      inputRef.current.value = medicine;
    }
    onChange(medicine);
    setShowDropdown(false);
    setSuggestions([]);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Handle focus - show suggestions if we have any
  const handleFocus = () => {
    const currentValue = inputRef.current?.value || '';
    if (currentValue.length >= 1 && suggestions.length > 0) {
      setShowDropdown(true);
    } else if (currentValue.length >= 1) {
      searchMedicines(currentValue);
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          defaultValue={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pr-8"
          autoComplete="off"
        />
        {isLoading ? (
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
            <div className="h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <Pill className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        )}
      </div>
      
      {showDropdown && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
        >
          {suggestions.map((medicine, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleSelect(medicine)}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 transition-colors ${
                highlightedIndex === index ? 'bg-blue-50 text-blue-700' : 'text-slate-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Pill className="h-3.5 w-3.5 text-slate-400" />
                <span>{medicine}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Standalone MedicineForm component to prevent re-renders
const MedicineForm = ({ medicines, onAddMedicine, onUpdateMedicine, onRemoveMedicine, isEditing }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between">
      <Label className="text-lg font-medium">Medicines</Label>
      <Button 
        type="button" 
        onClick={onAddMedicine}
        size="sm"
        variant="outline"
        className="border-blue-200 text-blue-600 hover:bg-blue-50"
      >
        <Plus className="h-3 w-3 mr-1" />Add Medicine
      </Button>
    </div>
    
    {medicines.map((medicine, index) => (
      <div key={medicine.id ?? index} className="flex gap-3 items-start p-4 border border-slate-200 rounded-lg bg-slate-50">
        <div className="flex-1 grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-sm">Medicine Name</Label>
            <MedicineAutocomplete
              value={medicine.medicine_name}
              onChange={(value) => onUpdateMedicine(index, 'medicine_name', value)}
              placeholder="e.g., Paracetamol 500mg"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Quantity</Label>
            <input 
              key={`qty-${medicine.id ?? index}`}
              defaultValue={medicine.quantity}
              onChange={(e) => onUpdateMedicine(index, 'quantity', e.target.value)}
              placeholder="e.g., 2 strips"
              required
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        </div>
        {medicines.length > 1 && (
          <Button 
            type="button"
            onClick={() => onRemoveMedicine(index)}
            size="icon"
            variant="ghost"
            className="text-red-500 hover:text-red-700 hover:bg-red-50 mt-7"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    ))}
  </div>
);

const PendingDeliveries = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [counts, setCounts] = useState({ active: 0, archived: 0 });
  const medicineIdCounter = useRef(1);
  
  const [newDelivery, setNewDelivery] = useState({
    customer_name: '',
    customer_number: '',
    customer_address: '',
    medicines: [{ id: 0, medicine_name: '', quantity: '', status: 'pending' }]
  });

  const fetchCounts = useCallback(async () => {
    try {
      const [activeRes, archivedRes] = await Promise.all([
        axios.get(`${API}/deliveries?delivery_status=active`, { headers: getAuthHeaders() }),
        axios.get(`${API}/deliveries?delivery_status=archived`, { headers: getAuthHeaders() })
      ]);
      setCounts({
        active: activeRes.data.length,
        archived: archivedRes.data.length
      });
    } catch (error) {
      console.error('Failed to fetch counts');
    }
  }, []);

  const fetchDeliveries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/deliveries?delivery_status=${activeTab}`, { 
        headers: getAuthHeaders() 
      });
      setDeliveries(response.data);
    } catch (error) {
      const message = handleApiError(error, 'Failed to fetch deliveries');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchDeliveries();
    fetchCounts();
  }, [activeTab, fetchDeliveries, fetchCounts]);

  // Print function for Pending Deliveries
  const handlePrint = useCallback(() => {
    const headers = [
      { label: 'Customer Name', align: 'left' },
      { label: 'Mobile', align: 'left' },
      { label: 'Medicines + Qty', align: 'left' },
      { label: 'Status', align: 'center' }
    ];

    const rows = deliveries.map(d => {
      const medicinesList = d.medicines?.map(m => `${m.medicine_name} (${m.quantity})`).join(', ') || '-';
      const statusBadge = d.status === 'delivered' 
        ? '<span class="badge badge-green">Delivered</span>'
        : d.status === 'pending' 
        ? '<span class="badge badge-amber">Pending</span>'
        : '<span class="badge badge-blue">In Progress</span>';
      
      return [
        d.customer_name || '-',
        d.customer_number || '-',
        `<span class="medicines-list">${medicinesList}</span>`,
        statusBadge
      ];
    });

    const summary = [
      { label: 'Total Deliveries', value: deliveries.length.toString(), type: 'neutral' },
      { label: 'Active', value: counts.active.toString(), type: 'neutral' },
      { label: 'Archived', value: counts.archived.toString(), type: 'neutral' }
    ];

    const content = generatePrintContent({
      title: `Pending Deliveries (${activeTab === 'active' ? 'Active' : 'Archived'})`,
      businessName: 'Shri Kripa Pharmacy',
      dateRange: `As of ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`,
      headers,
      rows,
      summary
    });

    openPrintWindow(content);
    toast.success('Opening print preview...');
  }, [deliveries, activeTab, counts]);

  // Register Alt+P shortcut
  usePrintShortcut(handlePrint);

  // Customer search with debounce
  const searchCustomers = useCallback(async (query) => {
    if (query.length < 2) {
      setCustomerSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/pharmacy-customers/search?q=${encodeURIComponent(query)}`, {
        headers: getAuthHeaders()
      });
      setCustomerSuggestions(response.data);
      setShowSuggestions(response.data.length > 0);
    } catch (error) {
      console.error('Failed to search customers:', error);
    }
  }, []);

  const handleCustomerInputChange = (field, value, isEditing = false) => {
    if (isEditing) {
      setEditingDelivery({ ...editingDelivery, [field]: value });
    } else {
      setNewDelivery({ ...newDelivery, [field]: value });
    }
    
    // Clear existing timeout
    if (searchTimeout) clearTimeout(searchTimeout);
    
    // Set new timeout for search
    const timeout = setTimeout(() => {
      searchCustomers(value);
    }, 300);
    setSearchTimeout(timeout);
  };

  const selectCustomer = (customer, isEditing = false) => {
    const data = {
      customer_name: customer.name,
      customer_number: customer.phone,
      customer_address: customer.address || ''
    };
    
    if (isEditing) {
      setEditingDelivery({ ...editingDelivery, ...data });
    } else {
      setNewDelivery({ ...newDelivery, ...data });
    }
    setShowSuggestions(false);
    setCustomerSuggestions([]);
  };

  const addMedicineRow = (isEditing = false) => {
    const newId = medicineIdCounter.current++;
    const newMedicine = { id: newId, medicine_name: '', quantity: '', status: 'pending' };
    if (isEditing) {
      setEditingDelivery({
        ...editingDelivery,
        medicines: [...editingDelivery.medicines, newMedicine]
      });
    } else {
      setNewDelivery({
        ...newDelivery,
        medicines: [...newDelivery.medicines, newMedicine]
      });
    }
  };

  const removeMedicineRow = (index, isEditing = false) => {
    if (isEditing) {
      const updatedMedicines = editingDelivery.medicines.filter((_, i) => i !== index);
      setEditingDelivery({ ...editingDelivery, medicines: updatedMedicines });
    } else {
      const updatedMedicines = newDelivery.medicines.filter((_, i) => i !== index);
      setNewDelivery({ ...newDelivery, medicines: updatedMedicines });
    }
  };

  const updateMedicine = (index, field, value, isEditing = false) => {
    if (isEditing) {
      const updatedMedicines = [...editingDelivery.medicines];
      updatedMedicines[index][field] = value;
      setEditingDelivery({ ...editingDelivery, medicines: updatedMedicines });
    } else {
      const updatedMedicines = [...newDelivery.medicines];
      updatedMedicines[index][field] = value;
      setNewDelivery({ ...newDelivery, medicines: updatedMedicines });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Filter out empty medicines
    const validMedicines = newDelivery.medicines.filter(
      m => m.medicine_name.trim() && m.quantity.trim()
    );
    
    if (validMedicines.length === 0) {
      toast.error('Please add at least one medicine');
      return;
    }
    
    try {
      await axios.post(`${API}/deliveries`, {
        customer_name: newDelivery.customer_name,
        customer_number: newDelivery.customer_number,
        customer_address: newDelivery.customer_address,
        medicines: validMedicines.map(m => ({
          medicine_name: m.medicine_name,
          quantity: m.quantity,
          status: 'pending'
        }))
      }, { headers: getAuthHeaders() });
      
      toast.success('Delivery added successfully');
      setShowAdd(false);
      medicineIdCounter.current = 1;
      setNewDelivery({
        customer_name: '',
        customer_number: '',
        customer_address: '',
        medicines: [{ id: 0, medicine_name: '', quantity: '', status: 'pending' }]
      });
      fetchDeliveries();
      fetchCounts();
    } catch (error) {
      toast.error('Failed to add delivery');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    // Filter out empty medicines
    const validMedicines = editingDelivery.medicines.filter(
      m => m.medicine_name.trim() && m.quantity.trim()
    );
    
    if (validMedicines.length === 0) {
      toast.error('Please add at least one medicine');
      return;
    }
    
    try {
      await axios.put(`${API}/deliveries/${editingDelivery.id}`, {
        customer_name: editingDelivery.customer_name,
        customer_number: editingDelivery.customer_number,
        customer_address: editingDelivery.customer_address,
        medicines: validMedicines.map(m => ({
          medicine_name: m.medicine_name,
          quantity: m.quantity,
          status: m.status || 'pending'
        }))
      }, { headers: getAuthHeaders() });
      
      toast.success('Delivery updated successfully');
      setShowEdit(false);
      setEditingDelivery(null);
      fetchDeliveries();
      fetchCounts();
    } catch (error) {
      toast.error('Failed to update delivery');
    }
  };

  const openEditConfirm = (delivery) => {
    setEditingDelivery({ ...delivery });
    setShowEditConfirm(true);
  };

  const confirmEdit = () => {
    setShowEditConfirm(false);
    setShowEdit(true);
  };

  const handleMedicineStatus = async (deliveryId, medicineIndex, newStatus) => {
    try {
      const response = await axios.put(
        `${API}/deliveries/${deliveryId}/medicine/${medicineIndex}/status?new_status=${newStatus}`, 
        {}, 
        { headers: getAuthHeaders() }
      );
      
      if (response.data.all_done) {
        toast.success('All medicines done! Delivery archived.');
      } else {
        toast.success(`Medicine marked as ${newStatus}`);
      }
      fetchDeliveries();
      fetchCounts();
    } catch (error) {
      toast.error('Failed to update medicine status');
    }
  };

  const handleMarkAllDone = async (deliveryId) => {
    try {
      await axios.put(
        `${API}/deliveries/${deliveryId}/mark-all-done`, 
        {}, 
        { headers: getAuthHeaders() }
      );
      toast.success('All medicines marked as done');
      fetchDeliveries();
      fetchCounts();
    } catch (error) {
      toast.error('Failed to mark all as done');
    }
  };

  const handleUnarchive = async (deliveryId) => {
    try {
      await axios.put(
        `${API}/deliveries/${deliveryId}/unarchive`, 
        {}, 
        { headers: getAuthHeaders() }
      );
      toast.success('Delivery restored to active');
      fetchDeliveries();
      fetchCounts();
    } catch (error) {
      toast.error('Failed to unarchive delivery');
    }
  };

  const handleDelete = async (deliveryId) => {
    if (!window.confirm('Are you sure you want to delete this delivery?')) return;
    
    try {
      await axios.delete(`${API}/deliveries/${deliveryId}`, { headers: getAuthHeaders() });
      toast.success('Delivery deleted');
      fetchDeliveries();
      fetchCounts();
    } catch (error) {
      toast.error('Failed to delete delivery');
    }
  };

  const sendWhatsApp = (delivery) => {
    const phone = delivery.customer_number.replace(/[^0-9]/g, '');
    
    let medicineList = '';
    delivery.medicines.forEach((med, index) => {
      const statusIcon = med.status === 'done' ? '✅' : med.status === 'ordered' ? '📦' : '⏳';
      medicineList += `${index + 1}. ${med.medicine_name} - ${med.quantity} ${statusIcon}\n`;
    });
    
    const addressLine = delivery.customer_address ? `\nDelivery Address: ${delivery.customer_address}\n` : '';
    
    const message = `Hello ${delivery.customer_name},\n\nYour medicine orders:\n\n${medicineList}${addressLine}\nStatus: ⏳ Pending | 📦 Ordered | ✅ Done\n\nThank you!\nShri Kripa Pharmacy`;
    
    const whatsappUrl = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    toast.success('WhatsApp opened');
  };

  // Get medicine status styling
  const getMedicineStatusStyle = (status) => {
    switch (status) {
      case 'done':
        return {
          bg: 'bg-emerald-50 border-emerald-200',
          text: 'text-emerald-700',
          badge: 'bg-emerald-100 text-emerald-700'
        };
      case 'ordered':
        return {
          bg: 'bg-amber-50 border-amber-200',
          text: 'text-amber-700',
          badge: 'bg-amber-100 text-amber-700'
        };
      default: // pending
        return {
          bg: 'bg-slate-50 border-slate-100',
          text: 'text-slate-900',
          badge: 'bg-slate-100 text-slate-600'
        };
    }
  };

  // Customer suggestions dropdown component
  const CustomerSuggestions = ({ isEditing }) => {
    if (!showSuggestions || customerSuggestions.length === 0) return null;
    
    return (
      <div className="absolute z-50 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
        {customerSuggestions.map((customer) => (
          <button
            key={customer.id}
            type="button"
            onClick={() => selectCustomer(customer, isEditing)}
            className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b border-slate-100 last:border-0"
          >
            <div className="font-medium text-slate-900">{customer.name}</div>
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <Phone className="h-3 w-3" />{customer.phone}
              {customer.address && (
                <>
                  <span className="mx-1">•</span>
                  <MapPin className="h-3 w-3" />{customer.address}
                </>
              )}
            </div>
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-slate-500">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading tracking-tight text-slate-900 mb-2">
              Pending Deliveries
            </h1>
            <p className="text-slate-500">Manage medicine deliveries</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handlePrint} variant="outline" className="h-9" title="Print (Alt+P)">
              <Printer className="h-4 w-4 mr-1.5" />Print
            </Button>
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild>
              <Button 
                className="bg-blue-600 hover:bg-blue-700 text-white" 
                data-testid="add-delivery-button"
              >
                <Plus className="h-4 w-4 mr-2" />Add Delivery
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-heading tracking-tight">
                  Add New Delivery
                </DialogTitle>
                <DialogDescription>
                  Add a new delivery with customer details and medicines.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Customer Name with Auto-fill */}
                <div className="space-y-2 relative">
                  <Label className="flex items-center gap-2">
                    <User className="h-4 w-4" />Customer Name
                  </Label>
                  <div className="relative">
                    <Input 
                      value={newDelivery.customer_name} 
                      onChange={(e) => handleCustomerInputChange('customer_name', e.target.value)} 
                      onFocus={() => newDelivery.customer_name.length >= 2 && setShowSuggestions(customerSuggestions.length > 0)}
                      placeholder="Start typing to search..."
                      required 
                      className="h-12" 
                      data-testid="customer-name-input"
                    />
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  </div>
                  <CustomerSuggestions isEditing={false} />
                </div>
                
                {/* Customer Number */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />Customer Number
                  </Label>
                  <Input 
                    value={newDelivery.customer_number} 
                    onChange={(e) => handleCustomerInputChange('customer_number', e.target.value)} 
                    placeholder="Enter mobile number"
                    required 
                    className="h-12" 
                    data-testid="customer-number-input"
                  />
                </div>
                
                {/* Customer Address */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />Address
                  </Label>
                  <Input 
                    value={newDelivery.customer_address} 
                    onChange={(e) => setNewDelivery({ ...newDelivery, customer_address: e.target.value })} 
                    placeholder="Delivery address"
                    className="h-12" 
                    data-testid="customer-address-input"
                  />
                </div>
                
                <MedicineForm 
                  medicines={newDelivery.medicines} 
                  onAddMedicine={() => addMedicineRow(false)}
                  onUpdateMedicine={(index, field, value) => updateMedicine(index, field, value, false)}
                  onRemoveMedicine={(index) => removeMedicineRow(index, false)}
                  isEditing={false} 
                />
                
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white" 
                  data-testid="submit-delivery-button"
                >
                  Add Delivery
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Edit Confirmation Dialog */}
        <Dialog open={showEditConfirm} onOpenChange={setShowEditConfirm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-heading tracking-tight flex items-center gap-2">
                <Edit2 className="h-5 w-5 text-blue-600" />
                Edit Delivery?
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to edit this delivery? You can modify customer details and medicine items.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 mt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowEditConfirm(false)}
              >
                Cancel
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={confirmEdit}
              >
                Yes, Edit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Delivery Dialog */}
        <Dialog open={showEdit} onOpenChange={setShowEdit}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading tracking-tight">
                Edit Delivery
              </DialogTitle>
              <DialogDescription>
                Modify customer details and medicine items.
              </DialogDescription>
            </DialogHeader>
            {editingDelivery && (
              <form onSubmit={handleEditSubmit} className="space-y-4">
                {/* Customer Name with Auto-fill */}
                <div className="space-y-2 relative">
                  <Label className="flex items-center gap-2">
                    <User className="h-4 w-4" />Customer Name
                  </Label>
                  <div className="relative">
                    <Input 
                      value={editingDelivery.customer_name} 
                      onChange={(e) => handleCustomerInputChange('customer_name', e.target.value, true)} 
                      onFocus={() => editingDelivery.customer_name.length >= 2 && setShowSuggestions(customerSuggestions.length > 0)}
                      placeholder="Start typing to search..."
                      required 
                      className="h-12" 
                    />
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  </div>
                  <CustomerSuggestions isEditing={true} />
                </div>
                
                {/* Customer Number */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />Customer Number
                  </Label>
                  <Input 
                    value={editingDelivery.customer_number} 
                    onChange={(e) => handleCustomerInputChange('customer_number', e.target.value, true)} 
                    placeholder="Enter mobile number"
                    required 
                    className="h-12" 
                  />
                </div>
                
                {/* Customer Address */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />Address
                  </Label>
                  <Input 
                    value={editingDelivery.customer_address || ''} 
                    onChange={(e) => setEditingDelivery({ ...editingDelivery, customer_address: e.target.value })} 
                    placeholder="Delivery address"
                    className="h-12" 
                  />
                </div>
                
                <MedicineForm 
                  medicines={editingDelivery.medicines} 
                  onAddMedicine={() => addMedicineRow(true)}
                  onUpdateMedicine={(index, field, value) => updateMedicine(index, field, value, true)}
                  onRemoveMedicine={(index) => removeMedicineRow(index, true)}
                  isEditing={true} 
                />
                
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Save Changes
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('active')}
            className={`px-6 py-3 font-medium text-sm transition-colors relative ${
              activeTab === 'active'
                ? 'text-blue-600'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="active-tab"
          >
            Active Deliveries
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
              activeTab === 'active' 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-slate-100 text-slate-600'
            }`}>
              {counts.active}
            </span>
            {activeTab === 'active' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('archived')}
            className={`px-6 py-3 font-medium text-sm transition-colors relative ${
              activeTab === 'archived'
                ? 'text-emerald-600'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="archived-tab"
          >
            Archived (Completed)
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
              activeTab === 'archived' 
                ? 'bg-emerald-100 text-emerald-700' 
                : 'bg-slate-100 text-slate-600'
            }`}>
              {counts.archived}
            </span>
            {activeTab === 'archived' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600" />
            )}
          </button>
        </div>

        {/* Status Legend */}
        {activeTab === 'active' && (
          <div className="flex items-center gap-4 text-xs text-slate-600 bg-slate-50 px-3 py-1.5 rounded-lg">
            <span className="font-medium">Status:</span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-slate-300"></span>
              Pending
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-400"></span>
              Ordered
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              Done
            </span>
          </div>
        )}

        {/* Delivery Cards - Grid Layout: 2 columns on desktop, 1 on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {deliveries.length === 0 ? (
            <Card className="col-span-full p-8 text-center border-slate-200">
              <Archive className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 text-sm mb-3">
                {activeTab === 'active' 
                  ? 'No pending deliveries' 
                  : 'No archived deliveries'}
              </p>
              {activeTab === 'active' && (
                <Button 
                  onClick={() => setShowAdd(true)} 
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Add First Delivery
                </Button>
              )}
            </Card>
          ) : (
            deliveries.map((delivery) => (
              <Card key={delivery.id} className="p-3 border-slate-200 hover:shadow-md transition-shadow" data-testid={`delivery-card-${delivery.id}`}>
                {/* Compact Customer Header with Actions */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm text-slate-900 truncate">
                      {delivery.customer_name}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 mt-0.5 text-xs text-slate-500">
                      <span className="flex items-center gap-0.5">
                        <Phone className="h-3 w-3" />
                        {delivery.customer_number}
                      </span>
                      {delivery.customer_address && (
                        <span className="flex items-center gap-0.5 truncate max-w-[120px]">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{delivery.customer_address}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Compact Action Buttons */}
                  <div className="flex gap-1 flex-shrink-0">
                    {activeTab === 'active' ? (
                      <>
                        <Button 
                          onClick={() => openEditConfirm(delivery)}
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                          data-testid={`edit-${delivery.id}`}
                          title="Edit"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button 
                          onClick={() => handleMarkAllDone(delivery.id)}
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50"
                          data-testid={`mark-all-done-${delivery.id}`}
                          title="Mark All Done"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button 
                          onClick={() => sendWhatsApp(delivery)} 
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-slate-500 hover:text-green-600 hover:bg-green-50"
                          data-testid={`whatsapp-button-${delivery.id}`}
                          title="WhatsApp"
                        >
                          <MessageCircle className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button 
                          onClick={() => handleUnarchive(delivery.id)}
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                          data-testid={`unarchive-${delivery.id}`}
                          title="Restore"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                        <Button 
                          onClick={() => handleDelete(delivery.id)}
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-slate-500 hover:text-red-600 hover:bg-red-50"
                          data-testid={`delete-${delivery.id}`}
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Compact Medicines List */}
                <div className="space-y-1">
                  {delivery.medicines && delivery.medicines.map((med, idx) => {
                    const status = med.status || (med.is_done ? 'done' : 'pending');
                    const statusColors = {
                      pending: { bg: 'bg-slate-50', text: 'text-slate-700', dot: 'bg-slate-400' },
                      ordered: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400' },
                      done: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' }
                    };
                    const colors = statusColors[status] || statusColors.pending;
                    
                    return (
                      <div 
                        key={idx} 
                        className={`flex items-center gap-2 px-2 py-1.5 rounded ${colors.bg}`}
                      >
                        {/* Status dot */}
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${colors.dot}`} />
                        
                        {/* Medicine name + quantity inline */}
                        <div className="flex-1 min-w-0 flex items-center gap-2">
                          <span className={`text-xs font-medium truncate ${status === 'done' ? 'line-through opacity-70' : ''} ${colors.text}`}>
                            {med.medicine_name}
                          </span>
                          <span className={`text-xs ${colors.text} opacity-70`}>
                            × {med.quantity}
                          </span>
                        </div>
                        
                        {/* Compact action buttons */}
                        {activeTab === 'active' && (
                          <div className="flex gap-0.5 flex-shrink-0">
                            {status === 'pending' && (
                              <>
                                <button 
                                  onClick={() => handleMedicineStatus(delivery.id, idx, 'ordered')}
                                  className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors"
                                  data-testid={`order-medicine-${delivery.id}-${idx}`}
                                >
                                  Order
                                </button>
                                <button 
                                  onClick={() => handleMedicineStatus(delivery.id, idx, 'done')}
                                  className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                                  data-testid={`done-medicine-${delivery.id}-${idx}`}
                                >
                                  Done
                                </button>
                              </>
                            )}
                            {status === 'ordered' && (
                              <>
                                <button 
                                  onClick={() => handleMedicineStatus(delivery.id, idx, 'pending')}
                                  className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                                  data-testid={`undo-medicine-${delivery.id}-${idx}`}
                                >
                                  Undo
                                </button>
                                <button 
                                  onClick={() => handleMedicineStatus(delivery.id, idx, 'done')}
                                  className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                                  data-testid={`done-medicine-${delivery.id}-${idx}`}
                                >
                                  Done
                                </button>
                              </>
                            )}
                            {status === 'done' && (
                              <button 
                                onClick={() => handleMedicineStatus(delivery.id, idx, 'pending')}
                                className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors"
                                data-testid={`undo-medicine-${delivery.id}-${idx}`}
                              >
                                Undo
                              </button>
                            )}
                          </div>
                        )}
                        
                        {activeTab === 'archived' && status === 'done' && (
                          <span className="text-[10px] text-emerald-600 font-medium">✓</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Compact timestamp */}
                <div className="mt-2 pt-1.5 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-[10px] text-slate-400">
                    {activeTab === 'archived' && delivery.archived_at 
                      ? `Done: ${new Date(delivery.archived_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`
                      : new Date(delivery.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
                    }
                  </span>
                  <span className="text-[10px] text-slate-400">
                    {delivery.medicines?.length || 0} item{(delivery.medicines?.length || 0) !== 1 ? 's' : ''}
                  </span>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PendingDeliveries;
